import { ChangeDetectionStrategy, Component, computed, inject, OnInit, output, signal, ViewChild } from '@angular/core';
import { CreateBriefService } from '../../../briefs/service/create-brief/create-brief.service';
import { BriefAiService } from '../../services/brief-ai.service';
import { ProductSection } from '../../sections/product-section/product-section';
import { BriefBasicsSection } from '../../sections/brief-basics-section/brief-basics-section';
import { ContentSection } from '../../sections/content-section/content-section';
import { CreativeSection } from '../../sections/creative-section/creative-section';
import { AudienceSection } from '../../sections/audience-section/audience-section';
import { ScreeningSection } from '../../sections/screening-section/screening-section';
import { CompensationSection } from '../../sections/compensation-section/compensation-section';
import { BbSummaryCard } from '../../ui/bb-summary-card/bb-summary-card';
import { BbSwitch } from '../../ui/bb-switch/bb-switch';
import { HasRoleDirectiveDirective } from '../../../../common/directives/has-role/has-role-directive.directive';
import { BriefTemplateDrawer } from '@/app/atoms/brief-template-drawer/brief-template-drawer';
import { CreatedCreativeBriefTemplate } from '@/app/services/creative-brief-template/creative-brief-template';
import { AgencyPricing } from '@/app/pages/create-brief-basic/steps/agency-pricing/agency-pricing';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { BasePricingModel } from '@/app/common/general-types/pricing.dto';
import { environment } from '@/environments/environment';
import { GaService } from '@/app/services/ga/ga';
import type { CreateBriefRegularStepAction } from '@/app/services/ga/create-brief-regular.analytics';

interface WizardStep {
  id: 'agency-pricing' | 'product' | 'brief' | 'content' | 'creative' | 'audience' | 'screening' | 'review';
  label: string;
  sub: string;
  eyebrow: string;
  title: string;
  desc: string;
  ai?: 'creative' | 'persona';
  templates?: boolean;
}

interface ChecklistItem {
  label: string;
  ok: boolean;
}

@Component({
  selector: 'bb-wizard-flow',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgencyPricing, ProductSection, BriefBasicsSection, ContentSection, CreativeSection, AudienceSection, ScreeningSection, CompensationSection, BbSummaryCard, BbSwitch, HasRoleDirectiveDirective, BriefTemplateDrawer],
  templateUrl: './wizard-flow.html',
  styleUrls: ['./wizard-flow.scss'],
})
export class WizardFlow implements OnInit {

  @ViewChild('briefTemplateDrawer') private briefTemplateDrawer?: BriefTemplateDrawer;

  /** Services */
  public readonly service = inject(CreateBriefService);
  private readonly briefAi = inject(BriefAiService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly gaService = inject(GaService);
  /** Services - END */

  readonly launch = output<void>();

  private readonly baseSteps: WizardStep[] = [
    { id: 'product', label: 'Product', sub: "What's being promoted", eyebrow: 'Step 1 · Product', title: 'Choose a product', desc: 'Pick a product from your catalogue or add a new one. This anchors everything creators will feature.' },
    { id: 'brief', label: 'Brief basics', sub: 'Goal, title & volume', eyebrow: 'Step 2 · Basics', title: 'The fundamentals', desc: 'Name the brief, set the campaign goal, and decide how much content you need.' },
    { id: 'content', label: 'Content', sub: 'Pieces and placements', eyebrow: 'Step 3 · Content', title: 'What should each creator make?', desc: 'Set 1 to 4 unique videos, and where each video is delivered or posted.' },
    { id: 'creative', label: 'Creative direction', sub: "Concepts & do's", eyebrow: 'Step 4 · Creative', title: 'Direct the creative', desc: "Give creators concepts, a step-by-step shot list, and clear do's and don'ts." },
    { id: 'audience', label: 'Audience', sub: 'Demographics & persona', eyebrow: 'Step 5 · Audience', title: 'Who should this reach?', desc: 'Define the target audience and the ideal creator persona for matching.', ai: 'persona' },
    { id: 'screening', label: 'Screening', sub: 'Questions & settings', eyebrow: 'Step 6 · Screening', title: 'Qualify & configure', desc: 'Add screening questions, compliance docs, and final brief settings.' },
    { id: 'review', label: 'Review & launch', sub: 'Final check', eyebrow: 'Step 7 · Review', title: 'Ready to turn up the volume?', desc: 'Give your brief a final review, then push it live to the creator network.' },
  ];

  readonly steps = computed(() => {
    if (!this.isShoutAgency()) {
      return this.baseSteps;
    }
    return [
      {
        id: 'agency-pricing' as const,
        label: 'Pricing Options',
        sub: 'Set creator earnings',
        eyebrow: 'Step 1 · Pricing',
        title: 'Pricing options',
        desc: 'Choose or create a pricing model for this brief.',
      },
      ...this.baseSteps.map((step, index) => ({
        ...step,
        eyebrow: `Step ${index + 2} · ${step.label}`,
      })),
    ];
  });

  readonly current = signal<number>(0);
  readonly aiBusy = signal<boolean>(false);

  readonly step = computed(() => this.steps()[this.current()]);
  readonly isLast = computed(() => this.current() === this.steps().length - 1);
  readonly progressPct = computed(() => (this.current() / (this.steps().length - 1)) * 100);

  readonly checklist = computed<ChecklistItem[]>(() => [
    { label: 'Brief title', ok: !!this.service.basicInfoModel().briefTitle },
    { label: 'Product selected', ok: !!this.service.selectedProduct() },
    { label: 'Campaign goal', ok: !!this.service.selectedBriefGoal() },
    { label: 'Content type resolved', ok: (this.service.pieces()?.length ?? 0) > 0 },
    { label: 'Pricing model ready', ok: this.service.hasSelectedPricingModel() },
  ]);

  readonly essentialsDone = computed(() => this.checklist().filter((c) => c.ok).length);
  readonly essentialsComplete = computed(() => this.checklist().every((c) => c.ok));

  ngOnInit(): void {
    if (this.service.pieces()?.length) {
      const contentStep = this.steps().findIndex((step) => step.id === 'content');
      if (contentStep >= 0) {
        this.current.set(contentStep);
      }
    }
    this.trackStep(this.current(), 'viewed');
  }

  isShoutAgency(): boolean {
    return this.currentUserService.focussedAgency?.agency.id === environment.shoutAgencyId;
  }

  isDone(index: number): boolean {
    return index < this.current();
  }

  stepCircleClass(index: number): string {
    if (index === this.current()) {
      return 'border-brand-studio bg-brand-studio text-white';
    }
    if (this.isDone(index)) {
      return 'border-brand-studio bg-brand-studio/10 text-brand-studio-dark dark:text-brand-studio';
    }
    return 'border-border text-muted-foreground';
  }

  goTo(index: number): void {
    this.current.set(index);
    this.trackStep(index, 'viewed');
  }

  back(): void {
    if (this.current() > 0) {
      const previousIndex = this.current() - 1;
      this.current.set(previousIndex);
      this.trackStep(previousIndex, 'viewed');
    }
  }

  next(): void {
    if (this.current() < this.steps().length - 1) {
      const currentIndex = this.current();
      this.trackStep(currentIndex, 'completed');
      const nextIndex = currentIndex + 1;
      this.current.set(nextIndex);
      this.trackStep(nextIndex, 'viewed');
    }
  }

  onModelSelected(model: BasePricingModel | undefined): void {
    this.service.onModelSelected(model);
    if (model) {
      const productIdx = this.steps().findIndex((s) => s.id === 'product');
      if (productIdx >= 0) {
        this.current.set(productIdx);
        this.trackStep(productIdx, 'viewed');
      }
    }
  }

  async runAi(kind: 'creative' | 'persona'): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    this.aiBusy.set(true);
    try {
      if (kind === 'persona') {
        await this.briefAi.suggestPersona();
      } else {
        await this.briefAi.draftBrief('Create a strong starter brief considering the product and campaign.');
      }
    } finally {
      this.aiBusy.set(false);
    }
  }

  openTemplates() {
    console.log('openTemplates()');
    queueMicrotask(() => this.briefTemplateDrawer?.openDrawer());
  }

  onBriefTemplateCreated(created: CreatedCreativeBriefTemplate): void {
    console.log('onBriefTemplateCreated() -> ', created);
  }

  private trackStep(index: number, action: CreateBriefRegularStepAction): void {
    const step = this.steps()[index];
    if (!step) {
      return;
    }
    this.gaService.trackRegularBriefStep('wizard', step.id, action, {
      step_index: index,
      step_label: step.label,
    });
  }
}
